import Icon from './Icon.jsx';
import PathGrid from './PathGrid.jsx';
import { levelTitle, nextRank, XP_REWARDS } from '../lib/xp.js';
import { nextChapter } from '../lib/story.js';

/**
 * The homepage — first thing anyone sees, and for most people the only thing
 * they see before deciding whether this app is for them.
 *
 * Six sections, in the order somebody actually needs them: today's verse, a
 * word for who they are, where they stand, what they can do, how far along they
 * are, and who made it. Scripture goes first and the numbers go fourth, because
 * an app that opens on a dashboard is a productivity tool with verses in it.
 *
 * ── The two audiences ───────────────────────────────────────────────────────
 *
 * A first-time visitor and somebody on day forty need different things from the
 * same screen, and the difference is one line rather than two layouts. Zero XP
 * gets a welcome; anything else gets their streak and the thing they left
 * unfinished. Splitting the whole page in two would mean maintaining two
 * homepages, and the new user would never see what the app becomes.
 *
 * ── Fading in ───────────────────────────────────────────────────────────────
 *
 * Sections arrive a tenth of a second apart via --enter-delay, so the page
 * assembles rather than appearing. It is a small thing that reads as care, and
 * it costs nothing: the animation runs once, on mount, and is removed entirely
 * under prefers-reduced-motion, where a staggered delay is just a page that
 * loads slowly.
 */

/** Greeting that changes with the time of day — small touch, feels alive. */
function greeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function HomeScreen({
  verseOfDay,
  streak,
  stats,
  level,
  xp = 0,
  story,
  isPremium,
  trialAvailable = false,
  trialDays = 7,
  isBookmarked,
  onNavigate,
  onStartDeck,
  onToggleBookmark,
  onAsk,
  onUpgrade,
  onQuiz,
  onPlayGame,
}) {
  const storyNext = nextChapter(story);
  const rank = levelTitle(level.level);
  const next = nextRank(level.level);
  // Not zero XP: opening the app awards the daily visit before anyone has done
  // anything, so a first-time visitor already has 15 and the welcome would
  // never once be shown. "New" means they have not done any of it yet.
  const isNew =
    stats.known + stats.stillLearning + stats.chaptersRead + stats.questionsAnswered === 0;

  /*
   * Order matters more than it looks.
   *
   * Most people arriving here have come from a video about one of two things:
   * Who Are You? and Before or After. Landing them on a grid where the first
   * card is Flashcards and the game they came for is missing entirely asks
   * them to go hunting for it, and on a phone, seconds after a video, they
   * simply do not. So the two things people arrive *for* come first.
   */
  const cards = [
    {
      id: 'character',
      icon: 'people',
      accent: 'violet',
      title: 'Who Are You?',
      blurb: 'Find the Bible character you are most like',
      note: `+${XP_REWARDS.characterQuiz} XP once`,
      tone: 'reward',
      onSelect: onQuiz,
    },
    {
      id: 'beforeafter',
      icon: 'clock',
      accent: 'amber',
      title: 'Before or After',
      blurb: 'Two events. Which came first?',
      note: 'How far can you get?',
      onSelect: () => onPlayGame?.('beforeafter'),
    },
    {
      id: 'flashcards',
      icon: 'book',
      accent: 'blue',
      title: 'Flashcards',
      blurb: 'Memorise scripture through repetition',
      note: `+${XP_REWARDS.verseMarkedKnown} XP a card`,
      tone: 'reward',
      onSelect: () => onStartDeck({ type: 'all' }),
    },
    {
      id: 'quiz',
      icon: 'target',
      accent: 'gold',
      title: 'Quiz',
      blurb: 'Test what you actually remember',
      note: `+${XP_REWARDS.quizCorrect} XP a correct answer`,
      tone: 'reward',
      onSelect: () => onNavigate('practice'),
    },
    {
      id: 'ask',
      icon: 'dove',
      accent: 'emerald',
      title: 'Ask Scripture',
      blurb: 'Questions answered from the text itself',
      // Deliberately unrewarded: XP for sending messages pays people to send
      // messages, and every one costs real money to answer.
      note: 'Free every day',
      onSelect: () => onNavigate('chat'),
    },
    {
      id: 'story',
      // The daily verse card the brief asked for would have pointed at the hero
      // directly above it. The Story is the app's biggest thing and had no card
      // at all, so it takes the slot.
      icon: 'scroll',
      accent: 'amber',
      title: 'The Story',
      blurb: storyNext
        ? `Chapter ${stats.chaptersRead + 1} of ${stats.chaptersTotal} — ${storyNext.title}`
        : 'Every chapter walked, beginning to end',
      note: `+${XP_REWARDS.storyChapter} XP a chapter`,
      tone: 'reward',
      onSelect: () => onNavigate('story'),
    },
    {
      id: 'review',
      icon: 'renew',
      accent: 'crimson',
      title: 'Review',
      blurb: stats.stillLearning
        ? 'The verses that keep slipping away'
        : 'Nothing waiting — go and get some wrong',
      note: stats.stillLearning
        ? `+${XP_REWARDS.verseMarkedKnown} XP a card`
        : 'nothing to review',
      tone: stats.stillLearning ? 'reward' : undefined,
      ready: stats.stillLearning > 0,
      onSelect: () => onStartDeck({ type: 'review' }),
    },
  ];

  return (
    <section className="screen home">
      {/* ── Hero: today's verse ───────────────────────────────────────────── */}
      <section className="hero enter" style={{ '--enter-delay': '0ms' }}>
        <div className="hero__glow" aria-hidden="true" />
        <div className="hero__inner">
          <p className="hero__label">Your daily scripture</p>
          <blockquote className="hero__verse">{verseOfDay.text}</blockquote>
          <cite className="hero__ref">{verseOfDay.ref}</cite>

          <div className="hero__tools">
            <button
              type="button"
              className={`icon-btn icon-btn--sm${isBookmarked ? ' is-on' : ''}`}
              onClick={() => onToggleBookmark(verseOfDay.id)}
              aria-pressed={isBookmarked}
            >
              <Icon name="ribbon" size={14} />
              {isBookmarked ? 'Saved' : 'Save'}
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              onClick={() => onAsk(verseOfDay)}
            >
              <Icon name="dove" size={14} />
              Ask about this
            </button>
          </div>
        </div>
      </section>

      {/* ── Who you are ───────────────────────────────────────────────────── */}
      <p className="welcome enter" style={{ '--enter-delay': '100ms' }}>
        {isNew ? (
          <>
            <strong>Welcome to Scripture Journey.</strong> Your path through God’s Word
            starts here — pick anything below.
          </>
        ) : (
          <>
            <strong>{greeting()}.</strong>{' '}
            {streak.current > 0
              ? `${streak.current} day${streak.current === 1 ? '' : 's'} running.`
              : 'Your streak is waiting to restart.'}{' '}
            {storyNext ? `Next up: ${storyNext.title}.` : 'The whole story is behind you.'}
          </>
        )}
      </p>

      {/* ── Where you stand ───────────────────────────────────────────────── */}
      <div className="stat-rail enter" style={{ '--enter-delay': '200ms' }}>
        <StatPill icon="flame" value={streak.current} label="day streak" />
        <StatPill icon="star" value={xp.toLocaleString()} label="total XP" />
        <StatPill icon="radiance" value={level.level} label={rank} />
        <StatPill icon="book" value={stats.known} label="verses known" />
      </div>

      {/* ── What you can do ───────────────────────────────────────────────── */}
      <div className="enter" style={{ '--enter-delay': '300ms' }}>
        <PathGrid heading="Choose Your Path" cards={cards} />
      </div>

      {/* ── How far along ─────────────────────────────────────────────────── */}
      <section className="level-card enter" style={{ '--enter-delay': '400ms' }}>
        <div className="level-card__head">
          <span className="level-card__rank">{rank}</span>
          {next ? <span className="level-card__next">{next.title}</span> : null}
        </div>

        <div
          className="level-card__track"
          role="progressbar"
          aria-valuenow={Math.round(level.progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progress to level ${level.level + 1}`}
        >
          <div className="level-card__fill" style={{ width: `${level.progress * 100}%` }} />
        </div>

        <div className="level-card__foot">
          <span>
            Level {level.level} · {level.into} of {level.needed} XP
          </span>
          <span>{level.needed - level.into} to go</span>
        </div>

        <p className="level-card__line">
          {next
            ? `Keep going — ${next.title} awaits at level ${next.atLevel}.`
            : 'Scribe. There is no rank above this one.'}
        </p>
      </section>

      {!isPremium ? (
        <button
          type="button"
          className="upsell enter"
          style={{ '--enter-delay': '500ms' }}
          onClick={onUpgrade}
        >
          <span className="upsell__glyph">
            <Icon name="radiance" size={20} />
          </span>
          <span>
            <strong>
              {trialAvailable ? `Try Premium free for ${trialDays} days` : 'Open the whole library'}
            </strong>
            <span>
              {trialAvailable
                ? `All ${stats.versesTotal} verses and every question, free for ${trialDays} days. Cancel before it ends and you pay nothing.`
                : `All ${stats.versesTotal} verses, every question, and the paths through each book.`}
            </span>
          </span>
          <span className="upsell__chev">
            <Icon name="arrow" size={18} />
          </span>
        </button>
      ) : null}

      {/* ── Who made it ───────────────────────────────────────────────────── */}
      <footer className="home-footer enter" style={{ '--enter-delay': '600ms' }}>
        <span>Scripture Journey</span>
        <span className="home-footer__mark" aria-hidden="true">
          <Icon name="cross" size={13} />
        </span>
        <span>Built on God’s Word</span>
      </footer>
    </section>
  );
}

function StatPill({ icon, value, label }) {
  return (
    <div className="stat-pill">
      <span className="stat-pill__icon" aria-hidden="true">
        <Icon name={icon} size={16} />
      </span>
      <span className="stat-pill__value">{value}</span>
      <span className="stat-pill__label">{label}</span>
    </div>
  );
}
